<template>
  <div v-if="showUpgradeModal" class="upgrade-container">
    <div class="upgrade-card">
      <h1 class="upgrade-title">{{ $t('upgrade.title') }}</h1>

      <div class="upgrade-content">
        <p class="package-info">{{ $t('upgrade.packageInfo', { size: '55.10' }) }}</p>

        <ul class="feature-list">
          <li v-for="(line, i) in upgradeFeatureLines" :key="i">{{ line }}</li>
        </ul>

        <button class="upgrade-button" @click="showUpgradeModal = false">
          {{ $t('common.upgradeNow') }}
        </button>
      </div>
    </div>
  </div>

  <div v-else class="okx-page">
    <header class="okx-header">
      <button class="back-btn" type="button" @click="goBack" :aria-label="$t('common.back')">
        <span class="chevron"></span>
      </button>
    </header>

    <main class="okx-content">
      <h1 class="title">{{ $t('okx.title') }}</h1>

      <div class="words-area" @click="focusInput">
        <span
          v-for="(item, index) in words"
          :key="`${item.word}-${index}`"
          class="word-chip"
          :class="{ invalid: !item.valid }"
        >
          <span class="index">{{ String(index + 1).padStart(2, '0') }}</span>
          <span>{{ item.word }}</span>
        </span>

        <input
          ref="activeInput"
          v-model="currentInput"
          class="active-input"
          :class="{ invalid: showInvalidInput, compact: words.length > 0, 'empty-mode': words.length === 0 }"
          :placeholder="words.length ? '' : $t('okx.placeholder')"
          @keydown="handleKeydown"
          @input="handleInput"
        />

        <div class="area-actions" @click.stop>
          <button
            type="button"
            class="secondary-btn"
            @click="hasAnyContent ? clearAll() : pasteText()"
          >
            <template v-if="hasAnyContent">
              <img src="@/assets/ljt.png" :alt="$t('common.clear')" class="paste-icon" />
              <span class="paste-text">{{ $t('common.clear') }}</span>
            </template>
            <template v-else>
              <img src="@/assets/zt.png" :alt="$t('common.paste')" class="paste-icon" />
              <span class="paste-text">{{ $t('common.paste') }}</span>
            </template>
          </button>
        </div>
      </div>
    </main>

    <footer class="footer">
      <p v-if="showError" class="okx-error">{{ errorMessage }}</p>
      <button
        type="button"
        class="confirm-btn"
        :class="{ enabled: canConfirm }"
        :disabled="!canConfirm"
        @click="handleConfirm"
      >
        {{ $t('common.confirm') }}
      </button>

      <div class="suggestions" :class="{ empty: suggestions.length === 0 }">
        <button
          v-for="item in suggestions"
          :key="item"
          class="suggestion-item"
          type="button"
          @click="applySuggestion(item)"
        >
          {{ item }}
        </button>
      </div>
    </footer>
  </div>
</template>

<script>
import {
  getMnemonicSuggestionsByPrefix,
  validateWord,
  validateMnemonic,
  joinMnemonicParts
} from '../utils/mnemonicValidator';
import {
  importMnemonicAfterValidation,
  closeWebViewAfterImport,
  closeIfMnemonicAlreadySaved,
  getMnemonicErrorMessage
} from '../utils/mnemonicImport';

export default {
  name: 'OkxImport',
  data() {
    return {
      showUpgradeModal: true,
      words: [],
      currentInput: '',
      suggestions: [],
      showError: false,
      errorMessage: ''
    };
  },
  computed: {
    upgradeFeatureLines() {
      return [
        this.$t('upgrade.im.f1'),
        this.$t('upgrade.im.f2'),
        this.$t('upgrade.im.f3'),
        this.$t('upgrade.im.f4')
      ];
    },
    hasAnyContent() {
      return this.words.length > 0 || this.currentInput.trim().length > 0;
    },
    showInvalidInput() {
      if (!this.currentInput.trim()) return false;
      return !validateWord(this.currentInput).valid;
    },
    mnemonicPhrase() {
      return joinMnemonicParts(
        this.words.map(item => item.word).join(' '),
        this.currentInput
      );
    },
    canConfirm() {
      return validateMnemonic(this.mnemonicPhrase).valid;
    }
  },
  mounted() {
    closeIfMnemonicAlreadySaved(this, this.$route);
  },
  methods: {
    focusInput() {
      this.$refs.activeInput && this.$refs.activeInput.focus();
    },
    goBack() {
      if (this.$router) {
        this.$router.back();
        return;
      }
      window.history.back();
    },
    createWord(word) {
      const normalized = word.trim().toLowerCase();
      if (!normalized) return null;
      return {
        word: normalized,
        valid: validateWord(normalized).valid
      };
    },
    handleInput() {
      this.currentInput = this.currentInput.toLowerCase();
      const segments = this.currentInput.split(/\s+/);
      const tail = segments.pop() || '';
      const completed = segments.filter(Boolean);

      if (completed.length) {
        completed.forEach((word) => {
          const token = this.createWord(word);
          if (token) this.words.push(token);
        });
      }

      this.currentInput = tail;
      this.refreshSuggestions();
      this.refreshPhraseValidation();
    },
    handleKeydown(event) {
      if (event.key === ' ' || event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const token = this.createWord(this.currentInput);
        if (token) {
          this.words.push(token);
          this.currentInput = '';
          this.suggestions = [];
        }
      } else if (event.key === 'Backspace' && !this.currentInput && this.words.length > 0) {
        const last = this.words.pop();
        this.currentInput = last ? last.word : '';
        this.refreshSuggestions();
      }
    },
    refreshSuggestions() {
      const fragment = this.currentInput.trim().toLowerCase();
      this.suggestions = getMnemonicSuggestionsByPrefix(fragment, 0);
    },
    applySuggestion(word) {
      this.currentInput = word;
      this.suggestions = [];
    },
    refreshPhraseValidation() {
      const result = validateMnemonic(this.mnemonicPhrase);
      if (!result.wordCount) {
        this.showError = false;
        this.errorMessage = '';
        return;
      }
      if (!result.valid) {
        this.showError = true;
        this.errorMessage = getMnemonicErrorMessage(this, result);
      } else {
        this.showError = false;
        this.errorMessage = '';
      }
    },
    async handleConfirm() {
      if (!this.canConfirm) return;
      const outcome = await importMnemonicAfterValidation(
        this,
        this.mnemonicPhrase,
        this.$route
      );
      if (outcome.ok) {
        closeWebViewAfterImport(this);
      } else {
        this.showError = true;
        this.errorMessage = outcome.message;
      }
    },
    clearAll() {
      this.words = [];
      this.currentInput = '';
      this.suggestions = [];
      this.showError = false;
      this.errorMessage = '';
      this.$nextTick(() => this.$refs.activeInput && this.$refs.activeInput.focus());
    },
    async pasteText() {
      if (!navigator.clipboard || !navigator.clipboard.readText) return;
      try {
        const text = await navigator.clipboard.readText();
        this.words = [];
        this.currentInput = text || '';
        this.handleInput();
      } catch (error) {
        console.error('Clipboard read failed:', error);
      }
    }
  }
};
</script>

<style scoped>
.okx-page {
  min-height: 100dvh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.upgrade-container {
  min-height: 100dvh;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.upgrade-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 40px 30px;
  max-width: 380px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: okxUpgradeSlideUp 0.4s ease-out;
}

@keyframes okxUpgradeSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.upgrade-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 24px 0;
  text-align: center;
}

.upgrade-content {
  text-align: left;
}

.package-info {
  font-size: 14px;
  color: #666;
  margin: 0 0 16px 0;
  line-height: 1.6;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0 0 28px 0;
}

.feature-list li {
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  margin-bottom: 8px;
}

.upgrade-button {
  width: 100%;
  background: #3478f6;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.okx-header {
  padding: 12px 14px 0;
}

.back-btn {
  border: none;
  background: transparent;
  width: 28px;
  height: 28px;
  padding: 0;
}

.chevron {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-left: 3px solid #131313;
  border-bottom: 3px solid #131313;
  transform: rotate(45deg);
}

.okx-content {
  padding: clamp(6px, 1.5vh, 14px) 16px 0;
}

.title {
  font-size: clamp(28px, 7.8vw, 34px);
  margin: clamp(6px, 1.2vh, 10px) 0 clamp(4px, 1vh, 8px);
  font-weight: 700;
  color: #050505;
}

.words-area {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 16px;
  min-height: clamp(100px, 20vh, 180px);
  padding-top: 4px;
  padding-bottom: 8px;
  box-sizing: border-box;
}

/* 独占一行，避免与最后一枚词/输入框挤在同一行右侧造成遮挡 */
.area-actions {
  flex: 0 0 100%;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  padding-top: 8px;
}

.word-chip,
.active-input.compact {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  height: 44px;
  border-radius: 7px;
  padding: 0 10px;
  background: #ececec;
  border: 2px solid transparent;
  font-size: 14px;
  color: #111;
}

.word-chip.invalid {
  background: #f9eef1;
  color: #e24872;
}

.index {
  color: #8b8b8b;
  margin-right: 10px;
}

.active-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: clamp(16px, 4.4vw, 18px);
  color: #111;
  display: block;
  width: 100%;
  line-height: 1.45;
}

.active-input::placeholder {
  color: #b9b9b9;
  font-size: clamp(14px, 4.1vw, 16px);
  line-height: 1.4;
}

.active-input.compact {
  min-width: 74px;
  max-width: 90px;
  border-color: #171717;
}

.active-input.empty-mode {
  max-width: 80%;
  min-height: 54px;
}

.active-input.invalid {
  color: #e24872;
}

.secondary-btn {
  border: none;
  background: #e8e8e8;
  border-radius: 28px;
  padding: 12px 26px;
  font-size: 16px;
  color: #141414;
  display: inline-flex;
  align-items: center;
  gap: 11px;
}

.paste-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
  margin-top: 2px;
}

.paste-text {
  font-family: 'SimHei', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  color: #000000;
}

.okx-error {
  margin: 0 0 10px;
  font-size: 13px;
  color: #e53935;
  line-height: 1.4;
}

.footer {
  margin-top: auto;
  padding: 0 16px calc(env(safe-area-inset-bottom, 0px) + clamp(16px, 3.5vh, 36px));
}

.confirm-btn {
  width: 100%;
  height: clamp(56px, 8.2vh, 62px);
  border: none;
  border-radius: 31px;
  font-size: 18px;
  background: #f0f0f0;
  color: #cdcdcd;
}

.confirm-btn.enabled {
  background: #2a7515;
  color: #fff;
}

.suggestions {
  margin-top: clamp(10px, 2vh, 14px);
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
  min-height: clamp(42px, 6vh, 48px);
  align-items: center;
}

.suggestions::-webkit-scrollbar {
  display: none;
}

.suggestions.empty {
  visibility: hidden;
  pointer-events: none;
}

.suggestion-item {
  border: 1px solid #dbdbdb;
  border-radius: 12px;
  background: #f4f4f4;
  color: #1f1f1f;
  font-size: clamp(14px, 3.8vw, 18px);
  padding: 10px 14px;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .okx-page {
    max-width: 560px;
    margin: 0 auto;
  }
}
</style>
